import { __rest } from "tslib";
import React from 'react';
import styled from 'styled-components';
import { blenderCss } from '@clds/blender';
import { globalTheme, useMixin } from '@clds/component-theme';
import { FoundationContextProvider, SurfaceContextProvider, } from '@clds/design-system-foundations';
import { Check } from '@clds/icon';
import { useItemContext } from './ItemContext.js';
import { itemLayoutSizeVariant, itemTheme, onelinerSizeVariant, twolinerSizeVariant, } from './theme';
export const ItemLayout = (_a) => {
    var { leftSlot, rightSlot, secondaryContent, children } = _a, rest = __rest(_a, ["leftSlot", "rightSlot", "secondaryContent", "children"]);
    const { size, isSelected, showCheckIconWhenSelected, selectionVariant, variant, } = useItemContext();
    const slotSize = useMixin(itemTheme.layout.normal.sizeVariant[size].slotSize);
    const surfaceTone = isSelected ? selectionVariant : 'subtle';
    const leftSlotContent = leftSlot && (React.createElement(SlotWrapper, { size: size },
        React.createElement(SurfaceContextProvider, { variant: variant, tone: surfaceTone },
            React.createElement(FoundationContextProvider, { size: slotSize }, leftSlot))));
    const mainContent = secondaryContent ? (React.createElement(TwolinerWrapper, { size: size },
        React.createElement(Line1Wrapper, { size: size, "data-test": "item--primary-content" }, children),
        React.createElement(Line2Wrapper, { size: size, "data-test": "item--secondary-content" }, secondaryContent))) : (React.createElement(OnelinerWrapper, { "data-test": "item--primary-content", size: size }, children));
    const rightSlotContent = rightSlot && (React.createElement(RightSlotWrapper, { size: size },
        React.createElement(SurfaceContextProvider, { variant: variant, tone: surfaceTone },
            React.createElement(FoundationContextProvider, { size: slotSize }, rightSlot))));
    const checkIcon = isSelected && showCheckIconWhenSelected && (React.createElement(SelectedIcon, { size: "sm", "data-test": "check-icon" }));
    return (React.createElement(Root, Object.assign({ size: size }, rest),
        leftSlotContent,
        mainContent,
        rightSlotContent,
        checkIcon));
};
const Root = styled.div `
  display: flex;
  align-items: center;
  text-align: left; // some inheritable styles might be transferred via popover rendered inside a button for example.
  > :first-child {
    margin-left: ${itemLayoutSizeVariant((variant) => variant.horizontalMargin)};
  }
  > * {
    margin-right: ${itemLayoutSizeVariant((variant) => variant.horizontalMargin)};
  }
`;
const SlotWrapper = styled.div `
  flex-shrink: 0;
  line-height: 0;
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: ${globalTheme.spacing.xs};
  }
  margin-top: ${itemLayoutSizeVariant((variant) => variant.slotVerticalMargin)};
  margin-bottom: ${itemLayoutSizeVariant((variant) => variant.slotVerticalMargin)};
`;
const RightSlotWrapper = styled(SlotWrapper) `
  height: 0; //buttons are bigger than foundation expects, for now we don't count them in height
`;
const Line1Wrapper = styled.div `
  display: flex;
  flex: 1;
  ${twolinerSizeVariant((branch) => blenderCss.typographyFromCssVariables(branch.typographySize.line1))};
`;
const Line2Wrapper = styled.div `
  ${twolinerSizeVariant((branch) => blenderCss.typographyFromCssVariables(branch.typographySize.line2))};
  margin-top: ${twolinerSizeVariant((branch) => branch.gap)};
`;
const TwolinerWrapper = styled.div `
  min-width: 0;
  flex: 1;
  margin-top: ${twolinerSizeVariant((branch) => branch.verticalMargin)};
  margin-bottom: ${twolinerSizeVariant((branch) => branch.verticalMargin)};
`;
const OnelinerWrapper = styled.div `
  min-width: 0;
  flex: 1;
  ${onelinerSizeVariant((branch) => blenderCss.typographyFromCssVariables(branch.typographySize))};
  margin-top: ${onelinerSizeVariant((branch) => branch.verticalMargin)};
  margin-bottom: ${onelinerSizeVariant((branch) => branch.verticalMargin)};
`;
const SelectedIcon = styled(Check) `
  margin-left: auto;
  flex-shrink: 0;
  margin-right: ${globalTheme.spacing.sm};
`;
//# sourceMappingURL=ItemLayout.js.map